import {
  Backpack,
  Brain,
  Check,
  Coins,
  Dumbbell,
  HeartPulse,
  ListChecks,
  Lock,
  Pickaxe,
  Recycle,
  Shield,
  ShieldOff,
  Trash2,
  UserRound,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { type EquipmentItem, type EquipmentSlot, type InventoryView } from "../types";
import { ELEMENT_LABELS, EquipmentElementMark } from "../ui/Elements";
import {
  EquipmentSlotIcon,
  type InventorySort,
  QUALITY_LABELS,
  QUALITY_RANK,
  SLOT_LABELS,
} from "../ui/EquipmentIcon";
import { EMPTY_PROGRESSION } from "../ui/defaults";
import { EquipmentDetail } from "./EquipmentDetail";

export function EquipmentScreen({
  inventory,
  goldCap,
  busy,
  onEquip,
  onUnequip,
  onLock,
  onEnhance,
  onReforge,
  onRefine,
  onCalibrate,
  onSell,
}: {
  inventory: InventoryView;
  goldCap: number;
  busy: boolean;
  onEquip: (itemId: string) => void;
  onUnequip: (slot: EquipmentSlot) => void;
  onLock: (itemId: string, locked: boolean) => void;
  onEnhance: (itemId: string) => void;
  onReforge: (itemId: string, affixIndex: number) => void;
  onRefine: (itemId: string, affixIndex: number) => void;
  onCalibrate: (itemId: string) => void;
  onSell: (itemIds: string[]) => void;
}) {
  const [slot, setSlot] = useState<"all" | EquipmentSlot>("all");
  const [sort, setSort] = useState<InventorySort>("itemLevel");
  const [selectedId, setSelectedId] = useState("");
  const [batchMode, setBatchMode] = useState(false);
  const [batch, setBatch] = useState<string[]>([]);
  const items = useMemo(() => {
    const filtered =
      slot === "all" ? [...inventory.items] : inventory.items.filter((item) => item.slot === slot);
    return filtered.sort((left, right) => {
      if (sort === "quality")
        return (
          QUALITY_RANK[right.quality] - QUALITY_RANK[left.quality] ||
          right.itemLevel - left.itemLevel
        );
      if (sort === "newest")
        return right.createdAt - left.createdAt || right.itemLevel - left.itemLevel;
      return (
        right.itemLevel - left.itemLevel || QUALITY_RANK[right.quality] - QUALITY_RANK[left.quality]
      );
    });
  }, [inventory.items, slot, sort]);
  const selected = inventory.items.find((item) => item.id === selectedId);
  const compared = selected
    ? inventory.items.find(
        (item) => item.id === inventory.equipped[selected.slot] && item.id !== selected.id,
      )
    : undefined;
  const progression = inventory.progression ?? EMPTY_PROGRESSION;
  const slots = Object.keys(SLOT_LABELS) as EquipmentSlot[];

  const chooseSlot = (nextSlot: "all" | EquipmentSlot) => {
    setSlot(nextSlot);
    setSelectedId("");
  };
  const remainingGoldCapacity = Math.max(0, goldCap - inventory.balances.gold);
  const batchGold = batch.reduce(
    (total, id) => total + (inventory.items.find((item) => item.id === id)?.sellValue ?? 0),
    0,
  );
  const batchCreditedGold = Math.min(batchGold, remainingGoldCapacity);
  const toggleBatch = (item: EquipmentItem) => {
    if (!item.canSell) return;
    setBatch((current) =>
      current.includes(item.id) ? current.filter((id) => id !== item.id) : [...current, item.id],
    );
  };
  const selectLowQuality = () => {
    setBatch(
      inventory.items
        .filter((item) => item.canSell && QUALITY_RANK[item.quality] <= QUALITY_RANK.uncommon)
        .map((item) => item.id),
    );
  };
  const confirmSell = (itemIds: string[]) => {
    const sellable = itemIds
      .map((id) => inventory.items.find((item) => item.id === id))
      .filter((item): item is EquipmentItem => Boolean(item?.canSell));
    if (sellable.length === 0) return;
    const totalGold = sellable.reduce((total, item) => total + item.sellValue, 0);
    const creditedGold = Math.min(totalGold, remainingGoldCapacity);
    const target = sellable.length === 1 ? `「${sellable[0].name}」` : `${sellable.length} 件装备`;
    let prompt = `出售${target}可获得 +${totalGold.toLocaleString("zh-CN")} 金币，确认出售？`;
    if (creditedGold === 0) {
      prompt = `金币仓库已满。出售${target}不会获得金币，确认出售？`;
    } else if (creditedGold < totalGold) {
      prompt = `出售${target}标价 +${totalGold.toLocaleString("zh-CN")} 金币，按当前仓库空间只能获得 +${creditedGold.toLocaleString("zh-CN")}，其余金币不会入账。确认出售？`;
    }
    if (!window.confirm(prompt)) return;
    onSell(sellable.map((item) => item.id));
    setBatch([]);
    setSelectedId("");
  };

  useEffect(() => {
    if (!selectedId) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedId("");
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedId]);

  return (
    <section className="inventory-dialog inventory-screen" aria-labelledby="inventory-title">
      <header className="inventory-header">
        <div>
          <h1 id="inventory-title">装备</h1>
        </div>
        <strong className="level-chip">Lv.{progression.level}</strong>
      </header>
      <div className="inventory-rpg-stats" aria-label="角色战斗属性">
        <span className="stat-attack">
          <Pickaxe size={16} />
          攻击<strong>{inventory.stats.attack}</strong>
        </span>
        <span className="stat-defense">
          <Shield size={16} />
          防御<strong>{inventory.stats.defense}</strong>
        </span>
        <span className="stat-health">
          <HeartPulse size={16} />
          生命<strong>{inventory.stats.maxHp}</strong>
        </span>
        <span className="stat-capacity">
          <Backpack size={16} />
          背包
          <strong>
            {inventory.items.length}/{inventory.capacity}
          </strong>
        </span>
      </div>
      {inventory.saleResult && (
        <div
          className={`inventory-sale-result${inventory.saleResult.discardedGold > 0 ? " is-capped" : ""}`}
          role="status"
        >
          <Coins size={17} />
          <span>已出售 {inventory.saleResult.itemIds.length} 件装备</span>
          <strong>
            {inventory.saleResult.creditedGold > 0
              ? `+${inventory.saleResult.creditedGold.toLocaleString("zh-CN")} 金币`
              : "金币未增加"}
          </strong>
          {inventory.saleResult.discardedGold > 0 && (
            <small>
              仓库空间不足，{inventory.saleResult.discardedGold.toLocaleString("zh-CN")} 金币未入账
            </small>
          )}
        </div>
      )}
      <div className="inventory-workspace">
        <aside className="loadout-panel" aria-label="当前穿戴">
          <div className="panel-heading">
            <strong>当前穿戴</strong>
            <UserRound size={20} />
          </div>
          <div className="equipment-figure">
            {slots.map((slotId) => {
              const item = inventory.items.find(
                (candidate) => candidate.id === inventory.equipped[slotId],
              );
              return (
                <button
                  key={slotId}
                  className={`gear-slot gear-slot-${slotId} ${item ? `quality-${item.quality}` : "is-empty"}`}
                  onClick={() => {
                    chooseSlot(slotId);
                    if (item) setSelectedId(item.id);
                  }}
                  aria-label={`${SLOT_LABELS[slotId]}：${item?.name ?? "未装备"}${item ? `，装等 ${item.itemLevel}` : ""}`}
                  title={`${SLOT_LABELS[slotId]}：${item?.name ?? "未装备"}${item ? ` · 装等 ${item.itemLevel}` : ""}`}
                >
                  <span className="gear-slot-meta">
                    <small>{SLOT_LABELS[slotId]}</small>
                    {item && (
                      <span className="gear-level" title={`装等 ${item.itemLevel}`}>
                        {item.itemLevel}
                      </span>
                    )}
                  </span>
                  <EquipmentSlotIcon
                    slot={slotId}
                    tier={item?.tier}
                    size={32}
                    legendaryId={item?.legendaryId}
                    templateId={item?.templateId}
                  />
                  <strong>{item?.name ?? "未装备"}</strong>
                </button>
              );
            })}
          </div>
          <div className="attribute-levels">
            <span>
              <Dumbbell size={14} />
              攻击<b>{progression.attack.level}</b>
            </span>
            <span>
              <Shield size={14} />
              防御<b>{progression.defense.level}</b>
            </span>
            <span>
              <Brain size={14} />
              智力<b>{progression.intelligence.level}</b>
            </span>
            <span>
              <HeartPulse size={14} />
              体质<b>{progression.constitution.level}</b>
            </span>
          </div>
        </aside>

        <section className="backpack-panel" aria-label="装备背包">
          <div className="panel-heading">
            <strong>背包</strong>
            <span>{items.length} 件</span>
          </div>
          <div className="inventory-toolbar">
            <select
              aria-label="筛选装备部位"
              value={slot}
              onChange={(event) => chooseSlot(event.target.value as "all" | EquipmentSlot)}
            >
              <option value="all">全部部位</option>
              {slots.map((slotId) => (
                <option key={slotId} value={slotId}>
                  {SLOT_LABELS[slotId]}
                </option>
              ))}
            </select>
            <select
              aria-label="装备排序方式"
              value={sort}
              onChange={(event) => setSort(event.target.value as InventorySort)}
            >
              <option value="itemLevel">装等优先</option>
              <option value="quality">品质优先</option>
              <option value="newest">最近获得</option>
            </select>
            <button
              className={`icon-button${batchMode ? " is-active" : ""}`}
              onClick={() => {
                setBatchMode((current) => !current);
                setBatch([]);
                setSelectedId("");
              }}
              title="多选出售"
              aria-label="多选出售"
              aria-pressed={batchMode}
            >
              <ListChecks size={14} />
            </button>
          </div>
          <div className="inventory-grid">
            {items.map((item) => (
              <button
                key={item.id}
                className={`inventory-tile quality-${item.quality}${selected?.id === item.id || batch.includes(item.id) ? " is-selected" : ""}${item.equipped ? " is-equipped" : ""}${item.locked ? " is-locked" : ""}${batchMode && !item.canSell ? " is-unsellable" : ""}${!item.requirementsMet ? " is-unavailable" : ""}`}
                onClick={() => (batchMode ? toggleBatch(item) : setSelectedId(item.id))}
                aria-pressed={batchMode ? batch.includes(item.id) : undefined}
                aria-label={`${item.name}，${QUALITY_LABELS[item.quality]}，装等 ${item.itemLevel}${item.slot === "weapon" && item.damageElement && item.damageElement !== "neutral" ? `，${ELEMENT_LABELS[item.damageElement]}属性` : ""}${item.locked ? "，已锁定" : ""}${!item.canSell ? "，不可出售" : ""}`}
                title={`${item.name} · ${QUALITY_LABELS[item.quality]} · 装等 ${item.itemLevel}`}
              >
                <span className="tile-meta">
                  <small className="tile-level" title={`装等 ${item.itemLevel}`}>
                    {item.itemLevel}
                  </small>
                  <span className="tile-status">
                    {item.equipped && (
                      <span className="equipped-mark" title="已装备">
                        <Check size={12} />
                      </span>
                    )}
                    {item.locked && (
                      <span className="locked-mark" title="已锁定">
                        <Lock size={12} />
                      </span>
                    )}
                    {!item.requirementsMet && (
                      <span className="unavailable-mark" title="穿戴需求未满足">
                        <ShieldOff size={12} />
                      </span>
                    )}
                    {batchMode && item.canSell && (
                      <span
                        className={`batch-mark${batch.includes(item.id) ? " is-checked" : ""}`}
                        aria-hidden="true"
                      >
                        {batch.includes(item.id) && <Check size={12} />}
                      </span>
                    )}
                  </span>
                </span>
                <span className="tile-icon">
                  <EquipmentSlotIcon
                    slot={item.slot}
                    tier={item.tier}
                    size={32}
                    legendaryId={item.legendaryId}
                    templateId={item.templateId}
                  />
                  {item.slot === "weapon" && <EquipmentElementMark element={item.damageElement} />}
                </span>
                <strong>
                  {item.name}
                  {item.enhancementLevel > 0 ? ` +${item.enhancementLevel}` : ""}
                </strong>
              </button>
            ))}
            {items.length === 0 && (
              <div className="inventory-empty">
                <Backpack size={26} />
                当前筛选没有装备
              </div>
            )}
          </div>
          {batchMode && (
            <div className="batch-sell-bar">
              <button
                className="secondary-command compact"
                disabled={busy}
                onClick={selectLowQuality}
              >
                <Recycle size={15} />
                选择绿装及以下
              </button>
              <span>
                已选 {batch.length} 件 ·{" "}
                {batchCreditedGold < batchGold
                  ? `可入账 +${batchCreditedGold.toLocaleString("zh-CN")}`
                  : `+${batchGold.toLocaleString("zh-CN")} 金币`}
              </span>
              <button
                className="danger-command compact"
                disabled={busy || batch.length === 0}
                onClick={() => confirmSell(batch)}
                title={
                  batchCreditedGold < batchGold
                    ? `仓库空间只能入账 ${batchCreditedGold.toLocaleString("zh-CN")} 金币`
                    : `出售并获得 ${batchGold.toLocaleString("zh-CN")} 金币`
                }
              >
                <Trash2 size={16} />
                出售
              </button>
            </div>
          )}
        </section>
      </div>
      {selected && (
        <>
          <EquipmentDetail
            item={selected}
            compared={compared}
            enhancementResult={
              inventory.enhancementResult?.itemId === selected.id
                ? inventory.enhancementResult
                : undefined
            }
            busy={busy}
            onEquip={onEquip}
            onUnequip={onUnequip}
            onLock={onLock}
            onEnhance={onEnhance}
            onReforge={onReforge}
            onRefine={onRefine}
            onCalibrate={onCalibrate}
            onSell={confirmSell}
            onClose={() => setSelectedId("")}
          />
        </>
      )}
    </section>
  );
}
